/**
 * jurisdictionFlag — turn a matter's stored jurisdiction into a flag code.
 *
 * THE BULGARIA BUG. `Flag` renders `fi fi-${code}` from the flag-icons
 * stylesheet, so it needs an ISO 3166-1 alpha-2 code and nothing else. The
 * three copies of `getJurisdictionDisplay` (Matters, ClientDetails, and the
 * matter Overview's own `getJurisdictionInfo`) all did the same thing:
 *
 *     const countryCode = countryCodeMap[code] || code;   // pass anything through
 *
 * …against a nine-entry map (SE, US, UK, GB, DE, FR, NO, DK, FI). Any
 * jurisdiction already stored as a plain alpha-2 happened to survive the
 * passthrough, which is why almost everything looked fine. Anything stored in
 * ANY other shape — a full country name, an alpha-3 code, a subdivision —
 * produced a class like `fi-bulgaria` or `fi-bgr`, which the stylesheet has no
 * rule for, so the element rendered as an empty box.
 *
 * The nine-entry map was never a mapping; it was a list of the countries
 * someone had checked. Bulgaria is simply the first jurisdiction in real use
 * that isn't in it and isn't already a bare alpha-2.
 *
 * So resolution is done properly here, once, for every caller:
 *
 *   alpha-2   "BG", "bg"          → BG   (validated, not assumed)
 *   alpha-3   "BGR"               → BG
 *   name      "Bulgaria"          → BG   (via `country-list`, already a dep)
 *   alias     "UK", "EL"          → GB, GR
 *   region    "England", "GB-ENG" → GB-ENG (flag-icons ships these)
 *   anything else                 → null, so no broken flag is rendered
 *
 * `null` is the important part. The old code could not say "I don't know",
 * so it emitted a class that silently rendered nothing — a blank gap with no
 * clue why. Callers now get an explicit null and skip the flag.
 */

import { getData, getName } from 'country-list';

/**
 * Codes that are NOT ISO alpha-2 countries but which flag-icons still ships.
 * Verified against `flag-icons/flags/4x3`.
 *
 * `EU` and `XK` matter because `getName` — the validity oracle below — is ISO
 * only and rejects both, so without this set a European Union or Kosovo
 * jurisdiction would resolve to null and render no flag even though the asset
 * is right there in the package.
 */
const EXTRA_FLAGS = new Set([
  'ES-CT', 'ES-GA', 'ES-PV', 'GB-ENG', 'GB-NIR', 'GB-SCT', 'GB-WLS',
  'EU', 'XK',
]);

/**
 * Names and legacy codes that ISO lookup alone gets wrong.
 *
 * `UK` is not an ISO code (the UK is `GB`) but it is what several older matter
 * rows hold. `EL` is the EU's code for Greece. The subdivisions are the
 * jurisdictions the product offers below country level.
 */
const ALIASES: Record<string, string> = {
  /*
    BU IS BULGARIA HERE.

    The backend stores "BU" for Bulgaria. That is not an ISO code for it —
    Bulgaria is BG — and historically BU was Burma (now MM), so this is a
    legacy/incorrect code specific to our data. flag-icons ships no `bu.svg`,
    so `fi-bu` matched no rule and the flag rendered as an empty box: the
    original Bulgaria bug, with the actual stored value now known.

    Burma is unaffected: it resolves through MM, which is a live ISO code.
  */
  BU: 'BG',
  UK: 'GB',
  EN: 'GB',
  EL: 'GR',
  ENGLAND: 'GB-ENG',
  SCOTLAND: 'GB-SCT',
  WALES: 'GB-WLS',
  'NORTHERN IRELAND': 'GB-NIR',
  CATALONIA: 'ES-CT',
  CATALUNYA: 'ES-CT',
  GALICIA: 'ES-GA',
  'BASQUE COUNTRY': 'ES-PV',
  // Regions with no flag of their own in flag-icons — fall back to the state.
  MADRID: 'ES',
  ANDALUSIA: 'ES',
  BAVARIA: 'DE',
  BERLIN: 'DE',
  HAMBURG: 'DE',
  // Common names for countries whose ISO name is something else entirely.
  'CZECH REPUBLIC': 'CZ',
  'SOUTH KOREA': 'KR',
  'NORTH KOREA': 'KP',
  RUSSIA: 'RU',
  VIETNAM: 'VN',
  IRAN: 'IR',
  SYRIA: 'SY',
  TAIWAN: 'TW',
  BOLIVIA: 'BO',
  VENEZUELA: 'VE',
  TANZANIA: 'TZ',
  MOLDOVA: 'MD',
  KOSOVO: 'XK',
  'EUROPEAN UNION': 'EU',
  'UNITED STATES': 'US',
  'UNITED STATES OF AMERICA': 'US',
  // ISO calls it "United Kingdom of Great Britain and Northern Ireland (the)".
  'UNITED KINGDOM': 'GB',
  'GREAT BRITAIN': 'GB',
};

/**
 * Normalised ISO name → alpha-2, built once from `country-list`'s own data.
 *
 * Needed because v2 ships the FORMAL ISO names: "Netherlands (Kingdom of the)",
 * "United States of America (the)", "Korea (the Republic of)". A plain
 * `getCode('Netherlands')` returns undefined against those, which is exactly
 * the class of silent miss that produced the blank flag. Indexing both the
 * full name and the name with its parenthetical stripped makes the everyday
 * spelling resolve without hand-maintaining a table of them.
 */
const NAME_INDEX: Map<string, string> = (() => {
  const index = new Map<string, string>();
  const norm = (value: string) =>
    value.toUpperCase().replace(/\([^)]*\)/g, ' ').replace(/[^A-Z ]/g, ' ')
      .replace(/\s+/g, ' ').trim();

  getData().forEach(({ code, name }: { code: string; name: string }) => {
    const full = norm(name);
    if (full && !index.has(full)) index.set(full, code);
    // "Korea the Republic of" → also index the leading segment, "Korea".
    const head = norm(name.split('(')[0]);
    if (head && !index.has(head)) index.set(head, code);
  });
  return index;
})();

/** The ISO name without its formal parenthetical — "Netherlands", not "Netherlands (Kingdom of the)". */
function displayName(code: string): string | undefined {
  const name = getName(code);
  if (!name) return undefined;
  const trimmed = name.replace(/\s*\([^)]*\)\s*/g, ' ').replace(/\s+/g, ' ').trim();
  return trimmed || name;
}

/*
  DELIBERATELY NOT ALIASED: the withdrawn codes with more than one successor —
  CS (Czechoslovakia, later Serbia and Montenegro), YU (Yugoslavia), SU (the
  Soviet Union), AN (Netherlands Antilles), DD (East Germany). Each could mean
  several modern states, so a guess would put a confidently wrong flag on a
  matter. They resolve to null and render no flag, which is the honest answer
  until we see one in real data.
*/

/**
 * ISO 3166-1 alpha-3 → alpha-2, for the jurisdictions the product lists.
 * `country-list` only speaks alpha-2, so this bridges the gap.
 */
const ALPHA3: Record<string, string> = {
  AUT: 'AT', BEL: 'BE', BGR: 'BG', HRV: 'HR', CYP: 'CY', CZE: 'CZ', DNK: 'DK',
  EST: 'EE', FIN: 'FI', FRA: 'FR', DEU: 'DE', GRC: 'GR', HUN: 'HU', IRL: 'IE',
  ITA: 'IT', LVA: 'LV', LTU: 'LT', LUX: 'LU', MLT: 'MT', NLD: 'NL', NOR: 'NO',
  POL: 'PL', PRT: 'PT', ROU: 'RO', SVK: 'SK', SVN: 'SI', ESP: 'ES', SWE: 'SE',
  CHE: 'CH', GBR: 'GB', USA: 'US', CAN: 'CA', AUS: 'AU', NZL: 'NZ', JPN: 'JP',
  CHN: 'CN', IND: 'IN', BRA: 'BR', MEX: 'MX', ISL: 'IS',
};

/** True when flag-icons actually ships a flag for this exact code. */
function isRenderable(code: string): boolean {
  if (EXTRA_FLAGS.has(code)) return true;
  // `getName` returns undefined for anything that is not a real alpha-2 code,
  // which makes it the validity oracle — no second hand-maintained list.
  return code.length === 2 && !!getName(code);
}

/**
 * The flag code for a jurisdiction, or `null` when there isn't one.
 *
 * Never throws and never guesses: an unrecognised value yields null so the
 * caller renders no flag rather than an empty box.
 */
export function resolveFlagCode(jurisdiction: string | null | undefined): string | null {
  if (!jurisdiction || typeof jurisdiction !== 'string') return null;

  const raw = jurisdiction.trim().replace(/\s+/g, ' ');
  if (!raw) return null;

  const key = raw.toUpperCase();

  const alias = ALIASES[key];
  if (alias) return alias;

  if (EXTRA_FLAGS.has(key)) return key;

  if (key.length === 2 && isRenderable(key)) return key;

  if (key.length === 3 && ALPHA3[key]) return ALPHA3[key];

  // A country name, in whatever case and spelling it was stored.
  const normalised = key.replace(/[^A-Z ]/g, ' ').replace(/\s+/g, ' ').trim();
  const byName = NAME_INDEX.get(normalised);
  if (byName && isRenderable(byName)) return byName;

  return null;
}

/**
 * The human label for a jurisdiction.
 *
 * The matter Overview kept a hand-written 27-country map for this, which is
 * why a Bulgarian matter showed "BG" where a Swedish one showed "Sweden" —
 * Bulgaria was simply missing from it. The ISO name is authoritative and needs
 * no maintenance; the raw value is the fallback so a bespoke jurisdiction
 * still reads as itself.
 */
export function resolveJurisdictionLabel(jurisdiction: string | null | undefined): string {
  if (!jurisdiction) return '';
  const raw = jurisdiction.trim();
  const code = resolveFlagCode(raw);
  if (code) {
    // Subdivisions have no ISO name — keep whatever was stored.
    const name = code.length === 2 ? displayName(code) : undefined;
    if (name) return name;
  }
  return raw;
}

export interface JurisdictionDisplay {
  /** What was stored, normalised for display next to the flag. */
  code: string;
  /** A code flag-icons can render, or null when there is none. */
  countryCode: string | null;
  /** The country's name, e.g. "Bulgaria". */
  label: string;
}

/** Everything a caller needs to render a jurisdiction, in one call. */
export function getJurisdictionDisplay(
  jurisdiction: string | null | undefined
): JurisdictionDisplay | null {
  if (!jurisdiction) return null;
  const raw = jurisdiction.trim();
  if (!raw) return null;
  return {
    code: raw.length <= 3 ? raw.toUpperCase() : raw,
    countryCode: resolveFlagCode(raw),
    label: resolveJurisdictionLabel(raw),
  };
}
